/**
 * Persists CPT table payloads for the parent order and optional linked orders.
 * @module updateCptTablesForOrderIds
 */
import {
  updateCPTtables,
  UpdateCPTtablesPayload,
} from "../../../../../api/postCall/updateCPTtables";
import {
  AuthOnFile,
  JCodeDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { SiblingOrder } from "../../../../../data-model/siblingOrdersModel";
import { resolveLinkedOrderUpdateOrderIds } from "../../../../../utils/resolveLinkedOrderUpdateOrderIds";
import { getAllJCodesFromAuthOnFile } from "../../../../singleOrderLayout/utils/existingDocsToLs";
import { applySiblingStatusMappingsForLaterDos } from "./applySiblingStatusMappingsForLaterDos";
import { filterCptUpdateOrderIds } from "./filterCptUpdateOrderIds";

/** @see resolveLinkedOrderUpdateOrderIds */
export const resolveCptUpdateOrderIds = resolveLinkedOrderUpdateOrderIds;

/** Parent + sibling snapshots used when propagating CPT saves to linked orders. */
export interface CptSiblingSaveContext {
  /** Parent order auth-on-file for J-code id remapping. */
  parentAuthOnFile?: AuthOnFile | null;
  /** Parent order date of service for sibling DOS rules. */
  parentDateOfService?: string;
  /** Filtered sibling orders from the data accumulator. */
  siblings?: SiblingOrder[];
}

/**
 * Re-maps shared CPT payload rows onto the J-code ids stored on a target order.
 *
 * @param jCodesFromPayload - J-code rows built from the parent CPT table
 * @param authOnFile - Target order's auth-on-file snapshot
 * @returns Payload rows with `id` aligned to the target order's J-code records
 */
const attachJCodeIdsFromAuthOnFile = (
  jCodesFromPayload: JCodeDetails[],
  authOnFile: AuthOnFile,
): JCodeDetails[] => {
  const targetOrderJCodes = getAllJCodesFromAuthOnFile(authOnFile) ?? [];

  return jCodesFromPayload.map((jCodeRow) => {
    const matchingJCode = jCodeRow.j_code
      ? targetOrderJCodes.find(
        (targetJCode) => targetJCode.j_code === jCodeRow.j_code,
      )
      : targetOrderJCodes.find(
        (targetJCode) =>
          targetJCode.id === jCodeRow.id || targetJCode.j_code === jCodeRow.id,
      );

    return {
      ...jCodeRow,
      id: matchingJCode?.id ?? jCodeRow.id,
    };
  });
};

/**
 * Resolves auth-on-file and DOS for a CPT update target order.
 *
 * @param targetOrderId - Order id receiving the CPT POST
 * @param parentOrderId - Order being edited on the CPT page
 * @param siblingContext - Parent and sibling snapshots from the save hook
 * @returns Per-order context for id remap and DOS rules
 */
const resolveCptTargetContext = (
  targetOrderId: string,
  parentOrderId: string,
  siblingContext?: CptSiblingSaveContext,
): { authOnFile?: AuthOnFile; dateOfService?: string } => {
  if (targetOrderId === parentOrderId) {
    return {
      authOnFile: siblingContext?.parentAuthOnFile ?? undefined,
      dateOfService: siblingContext?.parentDateOfService,
    };
  }

  const sibling = siblingContext?.siblings?.find(
    (linkedOrder) => linkedOrder.order_id === targetOrderId,
  );

  return {
    authOnFile: sibling?.auth_on_file,
    dateOfService: sibling?.order?.payload?.date_of_service,
  };
};

/**
 * Builds the CPT update request body for a single target order id.
 *
 * @param basePayload - Shared CPT payload from the parent table save
 * @param targetOrderId - Order id the update API should persist against
 * @param parentOrderId - Current order being edited on the CPT page
 * @param siblingContext - Parent and sibling snapshots for remap and DOS rules
 * @returns Request body scoped to {@link targetOrderId}
 */
const buildCptUpdatePayloadForOrderId = (
  basePayload: UpdateCPTtablesPayload,
  targetOrderId: string,
  parentOrderId: string,
  siblingContext?: CptSiblingSaveContext,
): UpdateCPTtablesPayload => {
  const targetContext = resolveCptTargetContext(
    targetOrderId,
    parentOrderId,
    siblingContext,
  );
  const jCodesFromPayload = basePayload.j_codes ?? [];
  const remappedJCodes = targetContext.authOnFile
    ? attachJCodeIdsFromAuthOnFile(jCodesFromPayload, targetContext.authOnFile)
    : jCodesFromPayload;
  const j_codes = applySiblingStatusMappingsForLaterDos(
    remappedJCodes,
    targetOrderId === parentOrderId,
    siblingContext?.parentDateOfService,
    targetContext.dateOfService,
  );

  return {
    ...basePayload,
    order_id: targetOrderId,
    j_codes,
  };
};

/**
 * Posts the same CPT payload for each target order id in parallel.
 *
 * When linked siblings are selected, each order receives the parent table edits
 * but J-code `id` values are re-aligned from that order's `auth_on_file` so the
 * backend updates the correct rows. When the payload includes a mapped parent
 * status ({@link CPT_SIBLING_STATUS_MAPPINGS}), same/earlier-DOS siblings are
 * skipped and later-DOS siblings receive the configured sibling status.
 *
 * @param basePayload - CPT payload built from the current table rows
 * @param parentOrderId - Current order being edited on the CPT page
 * @param selectedOrderIds - Optional linked sibling order ids from the dialog
 * @param siblingContext - Optional parent and sibling snapshots for propagation
 * @returns Resolves when every update request completes
 */
export const updateCptTablesForOrderIds = async (
  basePayload: UpdateCPTtablesPayload,
  parentOrderId: string,
  selectedOrderIds?: string[],
  siblingContext?: CptSiblingSaveContext,
): Promise<void> => {
  const resolvedOrderIds = resolveLinkedOrderUpdateOrderIds(
    parentOrderId,
    selectedOrderIds,
  );
  const orderIdsToUpdate = filterCptUpdateOrderIds(
    resolvedOrderIds,
    parentOrderId,
    siblingContext?.parentDateOfService,
    basePayload.j_codes,
    siblingContext?.siblings,
  );

  await Promise.all(
    orderIdsToUpdate.map((targetOrderId) =>
      updateCPTtables(
        buildCptUpdatePayloadForOrderId(
          basePayload,
          targetOrderId,
          parentOrderId,
          siblingContext,
        ),
      ),
    ),
  );
};
